import { PaginationComponent, PaginationMode } from "appworks/components/ui/pagination-component";
import { ButtonElement, ButtonEvent } from "appworks/graphics/elements/button-element";
import { Layers } from "appworks/graphics/layers/layers";
import { Services } from "appworks/services/services";
import { slotDefinition } from "slotworks/model/slot-definition";
import { slotModel } from "slotworks/model/slot-model";

export enum PayoutMode {
    LINES = "lines",
    WAYS = "ways",
    FIXED = "fixed",
    MULTIPLIER_ONLY = "multiplierOnly"
}

export class PaytableComponent extends PaginationComponent {

    /**
     * @deprecated use config object instead of static vars and constructor params
     */

    // Automatically set Layers.Background to "paytable" scene when paytable is shown, and "default" when it's hidden
    public static autoShowBackground: boolean = false;

    public static payoutMode: PayoutMode = PayoutMode.LINES;

    // The minimum bet in a ways game (if applicable)
    public static waysBaseBet: number;

    public static prependPayoutMatches: boolean = false;

    protected showTrailingDecimalsOnPayouts: boolean;

    constructor(contentLayer?: Layers,
                uiLayer?: Layers,
                mode: PaginationMode = PaginationMode.PAGIFY,
                paytableLayerOrder: string = "default",
                showTrailingDecimalsOnPayouts: boolean = false) {

        if (!contentLayer) {
            contentLayer = Layers.get("PaytableContent");
        }
        if (!uiLayer) {
            uiLayer = Layers.get("PaytableCommon");
        }

        super(contentLayer, uiLayer, mode, paytableLayerOrder);

        this.showTrailingDecimalsOnPayouts = showTrailingDecimalsOnPayouts;

        Services.bet.onBetChange.add(() => this.updatePayouts());

        uiLayer.onSceneEnter.add((scene: string) => {
            if (scene === "paytable") {
                this.show();
            } else {
                this.hide();
            }
        });
    }

    protected update() {
        super.update();

        this.updatePayouts();
    }

    protected onShow() {
        if (this.shouldGeneratePagePips()) {
            this.generatePagePips();
        }

        if (PaytableComponent.autoShowBackground && Layers.get("Background").hasScene("paytable")) {
            Layers.get("Background").setScene("paytable").execute();
        }
        super.onShow();
    }

    protected onHide() {
        if (PaytableComponent.autoShowBackground && Layers.get("Background").hasScene("paytable")) {
            Layers.get("Background").defaultScene().execute();
        }
        super.onHide();
    }

    protected shouldGeneratePagePips() {
        const btnCentre: ButtonElement = this.uiLayer.getButton("page_centre");
        const btnOffset: ButtonElement = this.uiLayer.getButton("page_offset");

        return Boolean(btnCentre && btnOffset);
    }

    protected generatePagePips() {
        const btnCentre: ButtonElement = this.uiLayer.getButton("page_centre");
        const btnOffset: ButtonElement = this.uiLayer.getButton("page_offset");

        const buttonDistance = {
            landscape: Math.abs(btnOffset.landscape.x - btnCentre.landscape.x),
            portrait: Math.abs(btnOffset.portrait.x - btnCentre.portrait.x)
        }
        const centreOffset={
            landscape: ((buttonDistance.landscape * this.totalPages) / 2) - (buttonDistance.landscape / 2),
            portrait:((buttonDistance.portrait * this.totalPages) / 2) - (buttonDistance.portrait / 2)
        }

        for (let i = 0; i < this.totalPages; i++) {
            const pageIndex = i;
            const pageName = this.contentSceneNames[pageIndex];

            const button = btnCentre.clone(pageName);
            this.uiLayer.add(button);
            this.uiLayer.addButton(pageName, button);

            button.landscape.x += (buttonDistance.landscape * i) - centreOffset.landscape;
            button.portrait.x += (buttonDistance.portrait * i) - centreOffset.portrait;

            const flexiButton = this.uiLayer.getFlexiButton(pageName);

            button.setEnabled(true);
            const listener = () => this.jumpToPage(pageIndex);
            button.on(ButtonEvent.CLICK, listener);
            this.pageJumpListeners.set(flexiButton, listener);

            if (pageIndex === this.page) { button.setEnabled(false); }
        }

        btnCentre.destroy(); // These were only needed as a reference
        btnOffset.destroy();
    }

    protected updatePayouts() {
        const symbols = slotDefinition.symbolDefinitions;
        const rtp = slotModel.read().regulations.rtp;

        if (!this.contentLayer || !this.contentLayer.getCurrentScene()) {
            return;
        }

        for (const symbol of symbols) {
            const payoutsText = this.contentLayer.getFlexiText("payouts_" + symbol.id);

            if (payoutsText) {
                let payoutText = "";
                if (symbol.payouts) {
                    let output;
                    (symbol.payouts as Map<string, number>).forEach((payout) => {
                        output = this.getPayoutValue(payout);

                        if (payoutText) {
                            payoutText = output + "\n" + payoutText;
                        } else {
                            payoutText = output;
                        }
                    });
                }

                payoutsText.setText(payoutText);
            }

            if (symbol.payouts) {
                symbol.payouts.forEach((payout, matches) => {
                    const payoutText = this.contentLayer.getFlexiText("payouts_" + symbol.id + "_" + matches);

                    const payoutValue = this.getPayoutValue(payout);
                    const matchesLabel = PaytableComponent.prependPayoutMatches ? `x${matches}: ` : "";

                    if (payoutText) {
                        if (`${payout}`.includes("respin")) {
                            const numRespins = parseInt(`${payout}`);
                            const suffix = Services.translations.get((numRespins === 1) ? "respin" : "respins").toLocaleUpperCase();
                            payoutText.setText(`${numRespins}x ${suffix}`);
                        } else {
                            payoutText.setText(`${matchesLabel}${payoutValue}`);
                        }
                    }
                });

            }
        }
    }

    protected getPayoutValue(payout: number): string {
        let amount;
        let toReturn = "";

        const stakePerLine = Services.bet.getCreditPerLine() * Services.bet.getCreditSize();

        if (PaytableComponent.payoutMode === PayoutMode.FIXED) {
            const betPerLine = (Services.bet.getFixedBetCost() / Services.bet.getSelectedLines());
            amount = payout * stakePerLine * betPerLine;
        } else if (PaytableComponent.payoutMode === PayoutMode.LINES) {
            amount = payout * stakePerLine;
        } else if (PaytableComponent.payoutMode === PayoutMode.WAYS) {
            amount = payout * Services.bet.getTotalStake() / PaytableComponent.waysBaseBet;
        } else {
            toReturn = payout.toString();
        }

        if (amount !== undefined) {
            // Format final amount if applicable, and round down in case of fractional "pence" wins
            toReturn = Services.currency.format(Math.floor(amount * 100) / 100, this.showTrailingDecimalsOnPayouts);
        }

        return toReturn.toString();
    }
}
